import type { ConversationSource } from "#types/types.ts";

// Matches a `[number]` citation marker that is not already a Markdown link (i.e. not followed by `(`).
const CITATION_MARKER_REGEX: RegExp = /\[(\d+)\](?!\()/g;

// Matches a citation reference: a positive integer from 1 to 1000.
const CITATION_REFERENCE_REGEX: RegExp = /^(?:[1-9]\d{0,2}|1000)$/;

// Stated explicitly so a citation reference is always read as decimal, never as another base.
const DECIMAL_BASE = 10;

/**
 * Prepares assistant answer text for Markdown rendering with inline citations.
 * Ensures a space between sentences and turns each `[number]` citation marker into a
 * `[number](#)` Markdown link so the renderer can replace it with a citation chip.
 */
export const formatCitedContent = (content: string): string => {
    // Ensure a space between sentences when content is appended after search.
    const contentWithProperSpacing = content.replace(/\.([A-Z])/g, ". $1");

    // Replace [number] with [number](#) for citations.
    return contentWithProperSpacing.replace(CITATION_MARKER_REGEX, (_, index) => `[${index}](#)`);
};

/** Returns `true` when the value is a citation reference (a positive integer from 1 to 1000). */
export const isCitationReference = (value?: string): boolean => {
    return CITATION_REFERENCE_REGEX.test(value ?? "");
};

/**
 * Extracts the citation index from a rendered Markdown link's text or href.
 * Returns the parsed index, or `null` when neither is a citation reference.
 */
export const parseCitationIndex = (text?: string, href?: string): number | null => {
    if (text && CITATION_REFERENCE_REGEX.test(text)) {
        return parseInt(text, DECIMAL_BASE);
    }

    if (href && CITATION_REFERENCE_REGEX.test(href)) {
        return parseInt(href, DECIMAL_BASE);
    }

    return null;
};

/** A source's identity across citation indexes: two indexes can name the same item. */
export const getSourceKey = ({ sourceType, id }: Pick<ConversationSource, "sourceType" | "id">) => `${sourceType}:${id}`;

/** A cited source and the number its pill shows. */
export interface NumberedCitation {
    source: ConversationSource;
    displayNumber: number;
}

export interface CitationNumbering {
    /** Each distinct cited source, one per source type and id, in display order. */
    citations: NumberedCitation[];
    /** The display number for each stored citation index. Two indexes that name one source share it. */
    numbersByIndex: ReadonlyMap<number, number>;
}

// Every marker the renderer turns into a pill: `[n]`, linked or not, and a link whose destination is
// the reference (`[Source](7)`, see parseCitationIndex).
const ANY_CITATION_MARKER_REGEX: RegExp = /\[(\d+)\]|\]\((\d+)\)/g;

// Code spans and fenced blocks render as code, not pills, so their markers take no number.
const CODE_REGEX: RegExp = /```[\s\S]*?```|`[^`\n]*`/g;

// Images render as images, not pills, even when their alt text is a number (`![1](chart.png)`).
const IMAGE_REGEX: RegExp = /!\[[^\]]*\]\([^)]*\)/g;

/**
 * Numbers an answer's citations 1..N in reading order. The stored index is a conversation-wide key
 * (it reads 187 or 303 deep into a conversation), so the pill shows this number instead and keeps the
 * stored index as the lookup key for its source. A marker with no source gets no number, as it gets
 * no pill.
 */
export const numberCitations = (
    content: string,
    resolveSource: (citationIndex: number) => ConversationSource | null | undefined
): CitationNumbering => {
    const numbersByIndex = new Map<number, number>();
    const numbersBySource = new Map<string, number>();
    const citations: NumberedCitation[] = [];

    for (const match of content.replace(CODE_REGEX, "").replace(IMAGE_REGEX, "").matchAll(ANY_CITATION_MARKER_REGEX)) {
        const reference = match[1] ?? match[2];
        const citationIndex = isCitationReference(reference) ? parseInt(reference, DECIMAL_BASE) : null;
        const source = citationIndex !== null && !numbersByIndex.has(citationIndex) ? resolveSource(citationIndex) : null;

        if (citationIndex === null || !source) {
            continue;
        }

        const sourceKey = getSourceKey(source);
        let displayNumber = numbersBySource.get(sourceKey);

        if (displayNumber === undefined) {
            displayNumber = citations.length + 1;
            numbersBySource.set(sourceKey, displayNumber);
            citations.push({ source, displayNumber });
        }

        numbersByIndex.set(citationIndex, displayNumber);
    }

    return { citations, numbersByIndex };
};
